import { Controller } from "@hotwired/stimulus"
import tippy, { roundArrow, hideAll } from 'tippy.js'

// Reference count for tippy instances using the scroll listener
let scrollListenerCount = 0

// Handler for main document scroll - only hides tooltips on main page scroll.
// Hide instantly: animating out while the anchor scrolls away reads as a
// size/position glitch.
const handleMainScroll = () => {
  hideAll({ duration: 0 })
}

// Check if an element's text is truncated
const isTruncated = (element) => {
  return element.scrollWidth > element.clientWidth
}

// Width of the widest visual line of text within an element. Every text rect is
// grouped into a line by vertical overlap, then we take the widest line's full
// horizontal span — using the span (not the widest single rect) so multi-column
// text is measured as a whole line rather than one cell.
const widestLine = (root) => {
  const rects = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  while (walker.nextNode()) {
    const node = walker.currentNode
    if (!node.textContent.trim()) continue
    const range = document.createRange()
    range.selectNodeContents(node)
    rects.push(...range.getClientRects())
  }
  if (rects.length === 0) return 0

  const lines = []
  for (const rect of rects) {
    const line = lines.find((l) => rect.top < l.bottom && rect.bottom > l.top)
    if (line) {
      line.left = Math.min(line.left, rect.left)
      line.right = Math.max(line.right, rect.right)
      line.top = Math.min(line.top, rect.top)
      line.bottom = Math.max(line.bottom, rect.bottom)
    } else {
      lines.push({ left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom })
    }
  }
  return Math.max(...lines.map((l) => l.right - l.left))
}

export default class extends Controller {
  static targets = ['origin', 'template', 'truncate']

  connect() {
    this.#createInstance()

    // Turbo morph preserves the origin but drops the popper (appended to
    // document.body at runtime), so connect() doesn't re-run — rebuild here.
    document.addEventListener('turbo:morph', this.#handleMorph)

    // Hide tooltips on page scroll only (not container scroll), via one
    // reference-counted listener shared across all tippy instances.
    if (scrollListenerCount === 0) {
      window.addEventListener('scroll', handleMainScroll)
    }
    scrollListenerCount++
  }

  disconnect() {
    this.instance?.destroy()
    document.removeEventListener('turbo:morph', this.#handleMorph)

    scrollListenerCount--
    if (scrollListenerCount === 0) {
      window.removeEventListener('scroll', handleMainScroll)
    }
  }

  #handleMorph = () => {
    if (this.instance && !document.body.contains(this.instance.popper)) {
      this.instance.destroy()
      this.#createInstance()
    }
  }

  #createInstance() {
    const origin = this.hasOriginTarget ? this.originTarget : this.element

    const {
      tippyContent,
      tippyPlacement,
      tippyArrow,
      tippyTrigger,
      tippyWidth,
      tippyWrapperClass,
      tippyDelay,
      tippyOffset,
      tippyTheme,
      tippyOnlyTruncated,
      tippyOnlyDisabled
    } = origin.dataset

    // data-tippy-only-truncated: only show tooltip when text overflows (scrollWidth > clientWidth)
    const onlyWhenTruncated = tippyOnlyTruncated !== undefined

    // data-tippy-only-disabled: only show tooltip while the wrapped control is disabled
    const onlyWhenDisabled = tippyOnlyDisabled !== undefined

    // tippy auto-reads data-tippy-offset as a bare number, which overrides our [0, N]
    // config (Popper ignores a bare offset). Drop it so the config wins.
    if (tippyOffset !== undefined) origin.removeAttribute("data-tippy-offset")

    if (tippyContent || this.hasTemplateTarget) {
      this.instance = tippy(origin, {
        content: tippyContent || this.templateTarget.innerHTML,
        allowHTML: true,
        placement: tippyPlacement || 'bottom-start',
        arrow: tippyArrow ? roundArrow : false,
        theme: tippyTheme || 'light',
        maxWidth: parseInt(tippyWidth) || undefined,
        trigger: tippyTrigger || undefined,
        interactive: true,
        // Keep aria-describedby (content: 'auto') so the tooltip text is exposed
        // to AT. Manage aria-expanded only on interactive references (portal
        // dropdown triggers); on non-interactive <div>s the attribute is
        // invalid (WCAG 4.1.2 / axe aria-allowed-attr).
        aria: { content: 'auto', expanded: origin.matches('button, [role="button"], a[href]') },
        delay: parseInt(tippyDelay) || undefined,
        offset: tippyOffset !== undefined ? [0, parseInt(tippyOffset)] : undefined,
        appendTo: document.body,
        onCreate: (instance) => {
          if (tippyWrapperClass) instance.popper.classList.add(tippyWrapperClass)
        },
        onShow: (instance) => {
          if (onlyWhenTruncated) {
            // Check the truncate target if available, otherwise check origin
            const truncateEl = this.hasTruncateTarget ? this.truncateTarget : instance.reference
            if (!isTruncated(truncateEl)) {
              return false // Cancel showing tooltip
            }
          }
          if (onlyWhenDisabled && !instance.reference.querySelector('[disabled], [aria-disabled="true"]')) {
            return false
          }
        },
        onMount: (instance) => {
          // Resize on mount (before the show transition paints) rather than
          // onShown, so the box never flashes at max-width and then shrinks.
          // Dropdowns manage their own width.
          if (tippyWrapperClass === 'tippy-dropdown') return

          const box = instance.popper.querySelector('.tippy-box')
          const content = instance.popper.querySelector('.tippy-content')
          if (!box || !content) return

          // The configured max-width — the cap that wrappable text obeys. Read
          // it from tippy's resolved props, not the live box style: tippy reuses
          // the same box across hovers, so the style still holds the previous
          // hugged width and the cap would drift smaller on every re-hover.
          const cap = instance.props.maxWidth

          // Measure the content's intrinsic (unwrapped) size, then put the cap
          // back and see whether the content had to wrap to fit it.
          box.style.maxWidth = 'none'
          box.style.width = 'max-content'
          const intrinsicWidth = box.getBoundingClientRect().width
          const intrinsicHeight = content.getBoundingClientRect().height
          box.style.width = ''
          box.style.maxWidth = Number.isFinite(cap) ? `${cap}px` : 'none'
          const wraps = content.getBoundingClientRect().height > intrinsicHeight + 0.5

          if (wraps) {
            // Wrappable text obeys the cap but text-wrap: balance leaves
            // whitespace to the right, so hug the widest rendered line.
            const widest = widestLine(content)
            if (widest > 0) {
              const contentStyles = getComputedStyle(content)
              const padding = parseFloat(contentStyles.paddingLeft) + parseFloat(contentStyles.paddingRight)
              const boxStyles = getComputedStyle(box)
              const border = parseFloat(boxStyles.borderLeftWidth) + parseFloat(boxStyles.borderRightWidth)
              // +1 guards against sub-pixel rounding that would re-wrap a line
              box.style.maxWidth = `${Math.ceil(widest + padding + border) + 1}px`
            }
          } else {
            // Non-wrapping content (icons, columns) would be clipped — and show
            // a scrollbar — at the cap, so size the box to its intrinsic width.
            box.style.maxWidth = `${Math.ceil(intrinsicWidth) + 1}px`
          }
          // Re-position so the box stays centered on the reference after resize.
          instance.popperInstance?.update()
        }
      })
    }
  }
}
